import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Business as DepartmentIcon,
  Work as PositionIcon,
  Grade as LevelIcon,
  EventBusy as LeaveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import useSettingsStore from '../../stores/settingsStore';
import useNotificationStore from '../../stores/notificationStore';
import { MagicLoader } from '../../design-system';

const HRConfiguration = () => {
  const theme = useTheme();
  const hrSettings = useSettingsStore(state => state.hrSettings);
  const fetchHRSettings = useSettingsStore(state => state.fetchHRSettings);
  const updateHRSetting = useSettingsStore(state => state.updateHRSetting);
  const isLoading = useSettingsStore(state => state.isLoading);
  const { showSuccess, showError } = useNotificationStore();
  
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, type: '', item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', item: null });
  
  // Use ref to track if we've already fetched
  const hasFetched = useRef(false);
  
  // Fetch HR settings on mount only once
  useEffect(() => {
    if (!hasFetched.current && !hrSettings && !isLoading) {
      hasFetched.current = true;
      fetchHRSettings();
    }
  }, []); // Empty dependency array to run only once on mount
  
  // Get data directly from store
  const departments = hrSettings?.departments || [];
  const positions = hrSettings?.positions || [];
  const employeeLevels = hrSettings?.employeeLevels || [];
  const leaveTypes = hrSettings?.leaveTypes || [];
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchTerm('');
  };
  
  // Filter items based on search
  const filterItems = (items) => {
    // Safety check - ensure items is an array
    if (!Array.isArray(items)) {
      return [];
    }
    
    if (!searchTerm) return items;
    
    const filtered = items.filter(item => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      return item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()));
    });
    
    return filtered;
  };
  
  // Handle add/edit dialog
  const handleOpenDialog = (type, item = null) => {
    setEditDialog({
      open: true,
      type,
      item: item || { name: '', description: '', active: true }
    });
  };
  
  const handleCloseDialog = () => {
    setEditDialog({ open: false, type: '', item: null });
  };
  
  // Handle delete dialog
  const handleOpenDeleteDialog = (type, item) => {
    setDeleteDialog({ open: true, type, item });
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, type: '', item: null });
  };
  
  // Handle save
  const handleSave = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const { type, item } = editDialog;
      let updatedSettings = { ...hrSettings };
      
      switch (type) {
        case 'department':
          if (item._id) {
            // Update existing
            updatedSettings.departments = (hrSettings?.departments || []).map(d => 
              d._id === item._id ? item : d
            );
          } else {
            // Add new
            item._id = Date.now().toString();
            updatedSettings.departments = [...(hrSettings?.departments || []), item];
          }
          break;
          
        case 'position':
          if (item._id) {
            updatedSettings.positions = (hrSettings?.positions || []).map(p => 
              p._id === item._id ? item : p
            );
          } else {
            item._id = Date.now().toString();
            updatedSettings.positions = [...(hrSettings?.positions || []), item];
          }
          break;
          
        case 'level':
          if (item._id) {
            updatedSettings.employeeLevels = (hrSettings?.employeeLevels || []).map(l => 
              l._id === item._id ? item : l
            );
          } else {
            item._id = Date.now().toString();
            updatedSettings.employeeLevels = [...(hrSettings?.employeeLevels || []), item];
          }
          break;
          
        case 'leave':
          if (item._id) {
            updatedSettings.leaveTypes = (hrSettings?.leaveTypes || []).map(l => 
              l._id === item._id ? item : l
            );
          } else {
            item._id = Date.now().toString();
            updatedSettings.leaveTypes = [...(hrSettings?.leaveTypes || []), item];
          }
          break;
      }
      
      await updateHRSetting(updatedSettings);
      showSuccess(`${type} ${item._id ? 'updated' : 'added'} successfully`);
      handleCloseDialog();
    } catch (error) {
      showError(`Failed to save ${editDialog.type}`);
    }
  };
  
  // Handle delete
  const handleDelete = async () => {
    try {
      const { type, item } = deleteDialog;
      let updatedSettings = { ...hrSettings };
      
      switch (type) {
        case 'department':
          updatedSettings.departments = (hrSettings?.departments || []).filter(d => d._id !== item._id);
          break;
        case 'position':
          updatedSettings.positions = (hrSettings?.positions || []).filter(p => p._id !== item._id);
          break;
        case 'level':
          updatedSettings.employeeLevels = (hrSettings?.employeeLevels || []).filter(l => l._id !== item._id);
          break;
        case 'leave':
          updatedSettings.leaveTypes = (hrSettings?.leaveTypes || []).filter(l => l._id !== item._id);
          break;
      }
      
      await updateHRSetting(updatedSettings);
      showSuccess(`${type} deleted successfully`);
      handleCloseDeleteDialog();
    } catch (error) {
      showError(`Failed to delete ${deleteDialog.type}`);
    }
  };
  
  // Render list items
  const renderListItem = (item, type) => {
    // Safety check for item
    if (!item || typeof item !== 'object') {
      return null;
    }
    
    return (
      <ListItem key={item._id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1">{item.name}</Typography>
              {item.code && (
                <Chip label={item.code} size="small" variant="outlined" />
              )}
              {!item.active && (
                <Chip label="Inactive" size="small" color="error" />
              )}
            </Box>
          }
          secondary={item.description}
        />
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            onClick={() => handleOpenDialog(type, item)}
            sx={{ mr: 1 }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            edge="end"
            onClick={() => handleOpenDeleteDialog(type, item)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };
  
  // Tab panels
  const tabPanels = [
    {
      label: 'Departments',
      icon: <DepartmentIcon />,
      type: 'department',
      items: departments,
      description: 'Manage organizational departments'
    },
    {
      label: 'Positions',
      icon: <PositionIcon />,
      type: 'position',
      items: positions,
      description: 'Manage job positions and titles'
    },
    {
      label: 'Employee Levels',
      icon: <LevelIcon />,
      type: 'level',
      items: employeeLevels,
      description: 'Manage employee hierarchy levels'
    },
    {
      label: 'Leave Types',
      icon: <LeaveIcon />,
      type: 'leave',
      items: leaveTypes,
      description: 'Manage types of leaves available'
    }
  ];
  
  
  if (isLoading && !hrSettings) {
    return <MagicLoader />;
  }
  
  return (
    <Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
            }
          }}
        >
          {tabPanels.map((panel, index) => (
            <Tab
              key={index}
              icon={panel.icon}
              label={panel.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>
      
      {tabPanels.map((panel, index) => (
        <Box
          key={index}
          hidden={activeTab !== index}
          sx={{ mt: 3 }}
        >
          {activeTab === index && (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">{panel.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {panel.description}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog(panel.type)}
                >
                  Add {panel.label.slice(0, -1)}
                </Button>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder={`Search ${panel.label.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              
              {(() => {
                const filteredItems = filterItems(panel.items);
                
                if (filteredItems.length === 0) {
                  return (
                    <Alert severity="info">
                      No {panel.label.toLowerCase()} found. Click "Add {panel.label.slice(0, -1)}" to create one.
                    </Alert>
                  );
                } else {
                  return (
                    <List>
                      {filteredItems.map((item) => renderListItem(item, panel.type))}
                    </List>
                  );
                }
              })()}
            </>
          )}
        </Box>
      ))}
      
      {/* Edit/Add Dialog */}
      <Dialog open={editDialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editDialog.item?._id ? 'Edit' : 'Add'} {editDialog.type}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSave}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={editDialog.item?.name || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  item: { ...editDialog.item, name: e.target.value }
                })}
                required
              />
            </Grid>
            
            {(editDialog.type === 'department' || editDialog.type === 'position') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Code"
                  value={editDialog.item?.code || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, code: e.target.value.toUpperCase() }
                  })}
                  helperText="Short code for identification"
                />
              </Grid>
            )}
            
            {editDialog.type === 'leave' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Default Days"
                    type="number"
                    value={editDialog.item?.defaultDays || 0}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      item: { ...editDialog.item, defaultDays: parseInt(e.target.value) || 0 }
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Carry Forward Limit"
                    type="number"
                    value={editDialog.item?.carryForwardLimit || 0}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      item: { ...editDialog.item, carryForwardLimit: parseInt(e.target.value) || 0 }
                    })}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editDialog.item?.description || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  item: { ...editDialog.item, description: e.target.value }
                })}
              />
            </Grid>
          </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={handleCloseDialog}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handleSave}
            disabled={!editDialog.item?.name}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.item?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(HRConfiguration);